import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  Id,
  InlineLoader,
  Moment,
  ModulePage,
  Notice,
  Select,
  TableSkeleton,
  TextInput,
  Toolbar,
  type Column,
} from '../../shell/kit';
import { AuditDiff, EntityRef, actorName, changeSummary } from '../../shell/audit';
import { useDetail, useResource, useTab } from '../../shell/hooks';
import { errorText } from '../../shell/errors';
import { useToast } from '../../shell/toast';
import { sites as loadSites, type SiteSummary } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import {
  fetchAudit,
  fetchEvents,
  fetchFacets,
  type AuditFilter,
  type AuditRow,
  type EventRow,
} from './api';
import { actionArea, actionVerb, makeAuditT, type AuditLocale, type AuditTranslate } from './i18n';

/**
 * Раздел «Журнал».
 *
 * Записи в `audit_entry` писались с первого дня — на каждой операции с
 * деньгами, бонусами и персональными данными. Прочитать их было нечем:
 * маршрута не существовало, и аудитор, которому ТЗ 8.3 обещает доступ на
 * чтение, мог открыть журнал только запросом в базу. Раздел закрывает ровно
 * это и ничего больше: здесь только чтение, ни одной кнопки, меняющей данные.
 *
 * Вторая вкладка — лента событий. Это другая сущность: журнал отвечает «кто это
 * сделал», лента — «что из этого вышло». Держать их рядом полезно, потому что
 * разбор инцидента идёт по обеим.
 */

type Tab = 'actions' | 'events';

const TABS: readonly Tab[] = ['actions', 'events'];

export default function AuditModule({ user, title }: ModuleViewProps) {
  const locale: AuditLocale = user.locale === 'kk' ? 'kk' : 'ru';
  const t = useMemo(() => makeAuditT(locale), [locale]);

  const [tab, setTab] = useTab<Tab>('audit', TABS, 'actions');
  const siteList = useResource(() => loadSites(), []);

  return (
    <ModulePage
      title={title}
      subtitle={t('audit.subtitle')}
      tabs={TABS.map((key) => ({ key, label: t(`audit.tab.${key}` as never) }))}
      activeTab={tab}
      onTab={setTab}
    >
      {tab === 'actions' ? (
        <ActionsTab t={t} sites={siteList.data ?? []} />
      ) : (
        <EventsTab t={t} sites={siteList.data ?? []} />
      )}
    </ModulePage>
  );
}

/* --------------------------------------------------------------- действия */

interface Draft {
  readonly action: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly actorId: string;
  readonly siteId: string;
  readonly from: string;
  readonly to: string;
}

const EMPTY: Draft = {
  action: '',
  entityType: '',
  entityId: '',
  actorId: '',
  siteId: '',
  from: '',
  to: '',
};

/** Дата из поля `<input type="date">` в границы суток. */
function dayStart(value: string): string | undefined {
  return value ? new Date(`${value}T00:00:00`).toISOString() : undefined;
}

function dayEnd(value: string): string | undefined {
  if (!value) return undefined;
  const next = new Date(`${value}T00:00:00`);
  next.setDate(next.getDate() + 1);
  return next.toISOString();
}

function ActionsTab({ t, sites }: { t: AuditTranslate; sites: readonly SiteSummary[] }) {
  const toast = useToast();
  const [detail, setDetail] = useDetail('audit', 'actions');
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [rows, setRows] = useState<readonly AuditRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [appending, setAppending] = useState(false);
  const [open, setOpen] = useState<AuditRow | null>(null);

  // Идентификатор записи может прийти из карточки клиента ссылкой
  // `#/audit/actions/<id>` — тогда фильтр по нему выставлен с самого начала.
  useEffect(() => {
    if (detail) setDraft((d) => (d.entityId === detail ? d : { ...d, entityId: detail }));
  }, [detail]);

  const facets = useResource(() => fetchFacets(30), []);

  const filter: AuditFilter = useMemo(
    () => ({
      ...(draft.action ? { action: draft.action } : {}),
      ...(draft.entityType ? { entityType: draft.entityType } : {}),
      ...(draft.entityId ? { entityId: draft.entityId.trim() } : {}),
      ...(draft.actorId ? { actorId: draft.actorId } : {}),
      ...(draft.siteId ? { siteId: draft.siteId } : {}),
      ...(dayStart(draft.from) ? { from: dayStart(draft.from) as string } : {}),
      ...(dayEnd(draft.to) ? { to: dayEnd(draft.to) as string } : {}),
      limit: 50,
    }),
    [draft],
  );

  const load = useCallback(
    async (append: boolean): Promise<void> => {
      if (append) setAppending(true);
      else setLoading(true);
      try {
        const page = await fetchAudit({
          ...filter,
          ...(append && cursor ? { cursor } : {}),
        });
        setRows((current) => (append ? [...current, ...page.items] : page.items));
        setCursor(page.nextCursor);
      } catch (err) {
        const shown = errorText(err);
        toast.error(shown.message, shown.code);
      } finally {
        setLoading(false);
        setAppending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, cursor],
  );

  // Смена фильтра начинает выборку заново: дочитывать страницы старого запроса
  // курсором нового — верный способ склеить два разных списка в один.
  useEffect(() => {
    setCursor(null);
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const columns: ReadonlyArray<Column<AuditRow>> = [
    {
      key: 'when',
      header: t('audit.col.when'),
      width: '11rem',
      render: (r) => <Moment iso={r.createdAt} />,
    },
    {
      key: 'who',
      header: t('audit.col.who'),
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate text-ink">{actorName(r.actor)}</div>
          {r.actor?.id ? (
            <button
              type="button"
              className="text-xs text-brand-700 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                setDraft((d) => ({
                  ...d,
                  actorId: d.actorId === r.actor?.id ? '' : (r.actor?.id ?? ''),
                }));
              }}
            >
              {draft.actorId === r.actor.id ? t('audit.filter.actorOff') : t('audit.filter.actorOn')}
            </button>
          ) : null}
        </div>
      ),
    },
    {
      key: 'what',
      header: t('audit.col.what'),
      render: (r) => (
        <div className="min-w-0">
          <div className="text-ink">{actionVerb(r.action)}</div>
          <div className="text-xs text-ink-muted">{actionArea(r.action)}</div>
        </div>
      ),
    },
    {
      key: 'entity',
      header: t('audit.col.entity'),
      render: (r) => <EntityRef type={r.entityType} id={r.entityId} />,
    },
    {
      key: 'site',
      header: t('audit.col.site'),
      render: (r) => {
        const site = sites.find((s) => s.id === r.siteId);
        return site ? <span>{site.code}</span> : <span className="text-ink-muted">—</span>;
      },
    },
    {
      key: 'changes',
      header: t('audit.col.changes'),
      render: (r) => <span className="text-xs text-ink-muted">{changeSummary(r)}</span>,
    },
  ];

  return (
    <>
      <Card title={t('audit.title')}>
        <Toolbar>
          <Field label={t('audit.filter.action')}>
            <Select
              value={draft.action}
              onChange={(e) => setDraft({ ...draft, action: e.target.value })}
            >
              <option value="">{t('audit.filter.any')}</option>
              {/* Название, а не код: искать `pos.order_refunded` в списке из
                  тридцати строк на латинице — занятие для того, кто и так знает
                  ответ. Сортируем по названию, раз показываем название. */}
              {[...(facets.data?.actions ?? [])]
                .map((a) => ({ ...a, title: actionVerb(a.value) }))
                .sort((a, b) => a.title.localeCompare(b.title, 'ru'))
                .map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.title} ({a.count})
                  </option>
                ))}
            </Select>
          </Field>
          <Field label={t('audit.filter.entity')}>
            <Select
              value={draft.entityType}
              onChange={(e) => setDraft({ ...draft, entityType: e.target.value })}
            >
              <option value="">{t('audit.filter.anyEntity')}</option>
              {(facets.data?.entityTypes ?? []).map((a) => (
                <option key={a.value} value={a.value}>
                  {a.value} ({a.count})
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('audit.filter.site')}>
            <Select
              value={draft.siteId}
              onChange={(e) => setDraft({ ...draft, siteId: e.target.value })}
            >
              <option value="">{t('audit.filter.anySite')}</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('audit.filter.from')}>
            <TextInput
              type="date"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            />
          </Field>
          <Field label={t('audit.filter.to')}>
            <TextInput
              type="date"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            />
          </Field>
          <Field label={t('audit.filter.entityId')} hint={t('audit.filter.entityIdHint')}>
            <TextInput
              value={draft.entityId}
              className="font-mono"
              onChange={(e) => {
                setDraft({ ...draft, entityId: e.target.value });
                if (detail) setDetail('');
              }}
            />
          </Field>
          <Button onClick={() => setDraft(EMPTY)}>{t('audit.filter.reset')}</Button>
          <Button onClick={() => void load(false)} disabled={loading}>
            {t('audit.filter.refresh')}
          </Button>
        </Toolbar>

        <DataTable
          loading={loading}
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id}
          onRowClick={(r) => setOpen(r)}
          empty={t('audit.empty')}
          emptyReason={t('audit.empty.reason')}
          pageSize={50}
          noun="записей"
        />

        {appending ? <InlineLoader /> : null}
        {cursor && !loading ? (
          <div className="mt-3">
            <Button onClick={() => void load(true)} disabled={appending}>
              {t('audit.more')}
            </Button>
          </div>
        ) : null}
      </Card>

      {open ? <EntryDrawer t={t} row={open} sites={sites} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function EntryDrawer({
  t,
  row,
  sites,
  onClose,
}: {
  t: AuditTranslate;
  row: AuditRow;
  sites: readonly SiteSummary[];
  onClose: () => void;
}) {
  const site = sites.find((s) => s.id === row.siteId);

  return (
    <Drawer title={t('audit.detail.title')} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <Card title={row.action}>
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <Line label={t('audit.col.when')}>
              <Moment iso={row.createdAt} />
            </Line>
            <Line label={t('audit.col.who')}>{actorName(row.actor)}</Line>
            <Line label={t('audit.col.entity')}>
              <EntityRef type={row.entityType} id={row.entityId} />
            </Line>
            <Line label={t('audit.col.site')}>{site ? `${site.code} · ${site.name}` : '—'}</Line>
            <Line label={t('audit.detail.request')}>
              {row.requestId ? <Id value={row.requestId} /> : '—'}
            </Line>
            <Line label={t('audit.detail.ip')}>{row.ip ?? '—'}</Line>
            <Line label={t('audit.detail.agent')}>
              <span className="text-xs break-all">{row.userAgent ?? '—'}</span>
            </Line>
          </dl>
        </Card>

        <Card title={`${t('audit.detail.before')} → ${t('audit.detail.after')}`}>
          <AuditDiff before={row.before} after={row.after} />
        </Card>

        <Notice tone="info">{t('audit.detail.masked')}</Notice>
      </div>
    </Drawer>
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line py-1.5 last:border-0">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right text-ink">{children}</dd>
    </div>
  );
}

/* ---------------------------------------------------------------- события */

function EventsTab({ t, sites }: { t: AuditTranslate; sites: readonly SiteSummary[] }) {
  const feed = useResource(() => fetchEvents({ limit: 200 }), []);

  const columns: ReadonlyArray<Column<EventRow>> = [
    {
      key: 'when',
      header: t('audit.col.when'),
      width: '11rem',
      render: (r) => <Moment iso={r.occurredAt} />,
    },
    { key: 'kind', header: t('audit.events.col.kind'), render: (r) => r.kind },
    {
      key: 'entity',
      header: t('audit.events.col.entity'),
      render: (r) => <EntityRef type={r.entityType ?? '—'} id={r.entityId} />,
    },
    {
      key: 'site',
      header: t('audit.col.site'),
      render: (r) => {
        const site = sites.find((s) => s.id === r.siteId);
        return site ? <span>{site.code}</span> : <span className="text-ink-muted">—</span>;
      },
    },
    {
      key: 'payload',
      header: t('audit.events.col.payload'),
      render: (r) => (
        <code className="text-xs break-all text-ink-muted">
          {JSON.stringify(r.payload ?? {}).slice(0, 160)}
        </code>
      ),
    },
  ];

  if (feed.error) {
    return <EmptyState title={t('audit.events.empty')} reason={feed.error.message} />;
  }

  return (
    <Card title={t('audit.tab.events')}>
      <p className="mb-3 text-sm text-ink-muted">{t('audit.events.hint')}</p>
      {feed.loading ? (
        <TableSkeleton rows={6} columns={columns.length} />
      ) : (
        <DataTable
          rows={feed.data ?? []}
          columns={columns}
          rowKey={(r) => r.id}
          empty={t('audit.events.empty')}
          emptyReason={t('audit.events.empty.reason')}
          pageSize={50}
          noun="событий"
        />
      )}
    </Card>
  );
}
